import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Card, Table, Alert } from 'react-bootstrap';
import callApi from "../../api/callApi";
import "../ViewExtractions/style.css";

const API_URL = process.env.REACT_APP_CREDIT_PROFILE_API_URL;

const ViewProfiles = () => {
    const navigate = useNavigate();
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadProfiles();
    }, []);

    const loadProfiles = async () => {
        try {
            setLoading(true);
            const response = await callApi(`${API_URL}/profiles`, 'GET');
            setProfiles((response && response.profiles) || []);
        } catch (error) {
            console.error('Error loading profiles:', error);
            setProfiles([]);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            const pad = (n) => n.toString().padStart(2, '0');
            return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
        } catch (error) {
            return dateString;
        }
    };

    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'completed': return 'badge bg-success';
            case 'processing': return 'badge bg-secondary';
            case 'failed': return 'badge bg-danger';
            default: return 'badge bg-secondary';
        }
    };

    const PROFILE_TYPE_LABELS = { sme: 'SME', sole_trader: 'Sole Trader', consumer: 'Consumer' };

    // Requested type; the tooltip shows what the classifier detected for each applicant.
    const renderProfileType = (profile) => {
        if (!profile.profile_type) return <span className="text-muted">-</span>;
        const detected = (profile.applicants || [])
            .map(a => `${a.account_holder_name || 'Applicant'}: detected ${a.detected_profile_type || '-'}`)
            .join('\n');
        return <span title={detected}>{PROFILE_TYPE_LABELS[profile.profile_type] || profile.profile_type}</span>;
    };

    // File names match what the API sends: one applicant -> that .xlsx, several -> a zip.
    const downloadName = (profile, kind) => {
        const shortId = profile.profile_id.slice(0, 8);
        const applicants = profile.applicants || [];
        if (kind === 'input') return `credit_profile_input_${shortId}.zip`;
        if (applicants.length === 1) return kind === 'workbook' ? applicants[0].workbook : applicants[0].profile_report;
        return `credit_profile_${kind}s_${shortId}.zip`;
    };

    const handleDownload = async (profile, kind) => {
        try {
            const url = `${API_URL}/profiles/${profile.profile_id}/download/${kind}`;
            const blob = await callApi(url, 'GET', null, true); // blob on 200; 404 is alerted by callApi
            if (!(blob instanceof Blob)) return;

            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = downloadName(profile, kind);
            document.body.appendChild(a);
            a.click();
            a.parentNode.removeChild(a);
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error(`Error downloading ${kind}:`, error);
            alert('Failed to download the file. Please try again.');
        }
    };

    const deleteProfile = async (profileId) => {
        if (window.confirm('Are you sure you want to delete this profile request? This action cannot be undone.')) {
            try {
                const response = await callApi(`${API_URL}/profiles/${profileId}`, 'DELETE');
                if (response && response.status === 'success') {
                    loadProfiles();
                } else {
                    alert('Failed to delete the profile request. Please try again.');
                }
            } catch (error) {
                console.error('Error deleting profile:', error);
                alert('Failed to delete the profile request. Please try again.');
            }
        }
    };

    const deleteAllProfiles = async () => {
        if (window.confirm('Are you sure you want to delete all profile requests? This action cannot be undone.')) {
            try {
                const response = await callApi(`${API_URL}/profiles`, 'DELETE');
                if (response && response.status === 'success') {
                    loadProfiles();
                } else {
                    alert('Failed to delete all profile requests. Please try again.');
                }
            } catch (error) {
                console.error('Error deleting all profiles:', error);
                alert('Failed to delete all profile requests. Please try again.');
            }
        }
    };

    const renderFileNames = (files) => {
        if (!files || files.length === 0) return <span className="text-muted">No files</span>;
        return (
            <div>
                {files.map((file, index) => (
                    <div key={index} className="text-truncate" style={{ maxWidth: '300px' }}
                         title={file.error ? `${file.file_name}: ${file.error}` : file.file_name}>
                        {file.file_name}
                    </div>
                ))}
            </div>
        );
    };

    if (loading) {
        return (
            <Container fluid className="mt-4">
                <div className="text-center">
                    <div className="spinner-border text-secondary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <div className="mt-2 text-muted">Loading credit profiles...</div>
                </div>
            </Container>
        );
    }

    return (
        <Container fluid className="mt-4 history-page">
            {/* Header */}
            <Row className="justify-content-center">
                <Col lg={12}>
                    <div className="text-center mb-5">
                        <h1 className="title mb-3">Credit Profiles</h1>
                    </div>
                </Col>
            </Row>

            <Row className="justify-content-center">
                <Col lg={12}>
                    {/* Action Icons */}
                    <div className="d-flex justify-content-end mb-3 gap-4">
                        <i
                            className="fas fa-sync-alt action-icon"
                            onClick={loadProfiles}
                            title="Refresh"
                        ></i>
                        <i
                            className="fas fa-plus-circle action-icon upload-icon"
                            onClick={() => navigate('/build-profile')}
                            title="Build New Profile"
                        ></i>
                        {profiles.length > 0 && (
                            <i
                                className="fas fa-trash-alt action-icon delete-files-icon"
                                onClick={deleteAllProfiles}
                                title="Delete All Profile Requests"
                            ></i>
                        )}
                    </div>

                    <Card className="shadow-sm">
                        <Card.Body className="p-0">
                            {profiles.length === 0 ? (
                                <Alert variant="info" className="m-4 text-center">
                                    <i className="fas fa-info-circle me-2"></i>
                                    No credit profiles found. Build a profile to see it here.
                                </Alert>
                            ) : (
                                <div style={{ overflowX: 'auto' }}>
                                    <Table striped hover className="mb-0" style={{ textAlign: 'left', minWidth: '800px' }}>
                                        <thead className="table-success">
                                            <tr>
                                                <th style={{ textAlign: 'left', width: '140px' }}>Date</th>
                                                <th style={{ textAlign: 'left', width: '300px' }}>File Names</th>
                                                <th style={{ textAlign: 'center', width: '120px' }}>Status</th>
                                                <th style={{ textAlign: 'center', width: '120px' }}>Profile Type</th>
                                                <th style={{ textAlign: 'center', width: '120px' }}>Provider</th>
                                                <th style={{ textAlign: 'center', width: '120px' }}>Files</th>
                                                <th style={{ textAlign: 'center', width: '200px' }}>Downloads</th>
                                                <th style={{ textAlign: 'center', width: '80px' }}>Delete</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {profiles.map((profile) => {
                                                const isCompleted = profile.status === 'completed';
                                                const isProcessed = isCompleted || profile.status === 'failed';
                                                return (
                                                    <tr key={profile.profile_id}>
                                                        <td style={{ verticalAlign: 'middle' }}>
                                                            {formatDate(profile.created_datetime)}
                                                        </td>
                                                        <td style={{ verticalAlign: 'middle', padding: '8px' }}>
                                                            {renderFileNames(profile.files)}
                                                        </td>
                                                        <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                                            <span className={getStatusBadgeClass(profile.status)} title={profile.error || ''}>
                                                                {profile.status ? profile.status.charAt(0).toUpperCase() + profile.status.slice(1) : 'Unknown'}
                                                            </span>
                                                        </td>
                                                        <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                                            {renderProfileType(profile)}
                                                        </td>
                                                        <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                                            {profile.provider_id || <span className="text-muted">-</span>}
                                                        </td>
                                                        <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                                            <div className="small">
                                                                <div>Total: <strong>{profile.processed_files || 0}</strong></div>
                                                                {profile.successful_files > 0 && (
                                                                    <div className="text-success">
                                                                        <i className="fas fa-check-circle me-1"></i>
                                                                        {profile.successful_files} processed
                                                                    </div>
                                                                )}
                                                                {profile.failed_files > 0 && (
                                                                    <div className="text-danger">
                                                                        <i className="fas fa-times-circle me-1"></i>
                                                                        {profile.failed_files} failed
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                                            <div className="d-flex gap-3 justify-content-center align-items-center">
                                                                <i
                                                                    className={`fas fa-file-pdf download-icon pdf-icon ${!isProcessed ? 'disabled' : ''}`}
                                                                    onClick={() => isProcessed ? handleDownload(profile, 'input') : null}
                                                                    title={isProcessed ? "Download Uploaded PDFs (ZIP)" : "Processing not complete"}
                                                                    style={{ cursor: !isProcessed ? 'not-allowed' : 'pointer' }}
                                                                ></i>
                                                                <i
                                                                    className={`fas fa-file-excel download-icon xlsx-icon ${!isCompleted ? 'disabled' : ''}`}
                                                                    onClick={() => isCompleted ? handleDownload(profile, 'workbook') : null}
                                                                    title={isCompleted ? "Download Credit Profile Workbook" : "No workbook available"}
                                                                    style={{ cursor: !isCompleted ? 'not-allowed' : 'pointer' }}
                                                                ></i>
                                                                <i
                                                                    className={`fas fa-file-excel download-icon xlsx-icon ${!isCompleted ? 'disabled' : ''}`}
                                                                    onClick={() => isCompleted ? handleDownload(profile, 'profile') : null}
                                                                    title={isCompleted ? "Download Dobin Credit Profile" : "No profile available"}
                                                                    style={{ cursor: !isCompleted ? 'not-allowed' : 'pointer' }}
                                                                ></i>
                                                            </div>
                                                        </td>
                                                        <td style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                                                            <i
                                                                className="fas fa-trash-alt delete-icon"
                                                                onClick={() => deleteProfile(profile.profile_id)}
                                                                title="Delete This Profile Request"
                                                                style={{ cursor: 'pointer' }}
                                                            ></i>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default ViewProfiles;
